import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ruler, DoorOpen, Plus, ClipboardList } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL =
  window.APP_CONFIG?.BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Read-only view of a customer's saved window-screen + screen-door
 * measurements from their most recent job. Reached from the Customer
 * card → More → "Saved Measurements".
 *
 * The data is mirrored onto `customers.saved_window_screens` and
 * `customers.saved_screen_doors` whenever a job is saved for this
 * customer (see `_mirror_measurements_to_customer` in
 * routers/jobs.py), so this page is literally just a pretty wrapper
 * around those two arrays.
 */
export default function CustomerSavedMeasurements() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getSessionToken();
    if (!token) {
      navigate("/login");
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUser(res.data))
      .catch(() => navigate("/login"));
  }, [navigate]);

  useEffect(() => {
    if (!user || !customerId) return;
    const token = getSessionToken();
    axios
      .get(`${API}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCustomer(res.data))
      .catch(() => toast.error("Could not load this customer"))
      .finally(() => setLoading(false));
  }, [user, customerId]);

  const windows = customer?.saved_window_screens || [];
  const doors = customer?.saved_screen_doors || [];
  const updatedAt = customer?.saved_measurements_updated_at;

  const totalScreens = windows.reduce(
    (sum, m) => sum + (m.quantity || 1),
    0
  );

  const formatSize = (v) => {
    // Values can be either a decimal (0.5) or a user-typed string
    // ("49 1/4"). Prefer the typed string when present.
    if (typeof v === "string" && v.trim()) return v.trim();
    if (typeof v === "number") return v.toString();
    return "?";
  };

  const startJob = () => {
    // Jump into Jobs with the customer preselected. The JobDialog
    // "Prefill from saved" button will fire from there.
    navigate(`/jobs?new=1&customer_id=${customerId}`);
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      <Header user={user} />

      <div className="max-w-md mx-auto p-4 space-y-4">
        <button
          onClick={() => navigate("/customers")}
          className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900"
          data-testid="back-to-customers"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Customers
        </button>

        {loading ? (
          <div className="text-center text-zinc-500 py-10">Loading…</div>
        ) : !customer ? (
          <div className="text-center text-zinc-500 py-10">
            Customer not found.
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <h1 className="text-xl font-bold text-zinc-900">
                {customer.fullName || "Customer"}
              </h1>
              <p className="text-sm text-zinc-500 mt-0.5">
                {customer.phoneNumber}
              </p>
              {updatedAt && (
                <p className="text-xs text-zinc-400 mt-2">
                  Saved {new Date(updatedAt).toLocaleString()}
                </p>
              )}
            </div>

            {/* Window screens */}
            <section
              className="bg-white rounded-xl border border-zinc-200 p-4"
              data-testid="saved-window-screens-section"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-[#207024]" />
                  <h2 className="font-semibold text-zinc-900">
                    Window Screens
                  </h2>
                </div>
                {windows.length > 0 && (
                  <span className="text-xs font-semibold text-[#207024] bg-green-50 px-2 py-1 rounded">
                    {totalScreens} total
                  </span>
                )}
              </div>
              {windows.length === 0 ? (
                <p className="text-sm text-zinc-500 italic">
                  No window screens saved yet.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {windows.map((m, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between bg-zinc-50 rounded px-3 py-1.5 text-sm font-mono"
                      data-testid={`saved-window-${i}`}
                    >
                      <span className="text-zinc-800">
                        {formatSize(m.width)}" x {formatSize(m.height)}"
                        {m.pull_tab ? (
                          <span className="ml-2 text-xs text-zinc-500 font-sans">
                            pull: {m.pull_tab}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-zinc-500 text-xs">
                        × {m.quantity || 1}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Screen doors */}
            <section
              className="bg-white rounded-xl border border-zinc-200 p-4"
              data-testid="saved-screen-doors-section"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <DoorOpen className="w-5 h-5 text-[#207024]" />
                  <h2 className="font-semibold text-zinc-900">
                    Screen Doors
                  </h2>
                </div>
                {doors.length > 0 && (
                  <span className="text-xs font-semibold text-[#207024] bg-green-50 px-2 py-1 rounded">
                    {doors.length} total
                  </span>
                )}
              </div>
              {doors.length === 0 ? (
                <p className="text-sm text-zinc-500 italic">
                  No screen doors saved yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {doors.map((d, i) => (
                    <li
                      key={i}
                      className="rounded-lg border border-zinc-200 bg-zinc-50 p-2.5"
                      data-testid={`saved-door-${i}`}
                    >
                      <div className="font-semibold text-sm text-zinc-900">
                        {d.type || "?"} · {d.color || "?"} · {d.mesh || "?"}
                      </div>
                      <div className="font-mono text-sm text-zinc-700 mt-0.5">
                        {formatSize(d.width)}" x {formatSize(d.height)}"
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Handle on {d.handle === "left" ? "left" : "right"}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <button
              onClick={startJob}
              disabled={windows.length === 0 && doors.length === 0}
              data-testid="start-job-with-saved-btn"
              className="w-full h-12 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Plus className="w-5 h-5" />
              Start New Job With These
            </button>

            {windows.length === 0 && doors.length === 0 && (
              <div className="text-center text-xs text-zinc-400 italic">
                Nothing to prefill yet — measurements save automatically after
                the first job is created for this customer.
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
